import { Injectable, inject } from '@angular/core';
import { SettingsValues } from '@shared/constants/settings-values';
import { ProjectSettingFormFieldConfig } from '@shared/models/domain/custom-views.model';
import { ProjectSettingsView } from '@shared/models/domain/domain-view.model';
import { ProjectSetting } from '@shared/models/domain/wms.model';
import { BulkRequest } from '@shared/models/request/bulk-request.model';
import { PageRequest } from '@shared/models/request/page-request.model';
import { CoreApiService } from '@shared/services/common/core-api.service';
import { ReadOnlyCoreApiService } from '@shared/services/common/read-only-core-api.service.';
import { Observable, forkJoin, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ProjectSettingsService extends CoreApiService<ProjectSetting> {
  private projectSettingsViewService = inject(ProjectSettingsViewService);

  constructor() {
    super('admin/project-settings');
  }

  saveSidemarkPricing(projectId: string, pricingItems: ProjectSettingFormFieldConfig[]): Observable<ProjectSetting[] | null> {
    if (!projectId || !pricingItems?.length) return of(null);

    const request = new PageRequest();
    request.currentPage = 1;
    request.itemsPerPage = 1000;
    request.noObserverEmit = true;
    request.find['projectId'] = projectId;

    return this.projectSettingsViewService.get(request).pipe(
      switchMap((response) => {
        const payload = this.buildSidemarkPricingPayload(projectId, pricingItems, response?.items ?? []);
        const tasks = payload.items?.map((item) => item?.id ? this.patch(item.id, item) : this.create(item)) ?? [];
        return tasks.length ? forkJoin(tasks) : of(null);
      }),
    );
  }

  private buildSidemarkPricingPayload(projectId: string, pricingItems: ProjectSettingFormFieldConfig[], currentSettings: ProjectSettingsView[]): BulkRequest<ProjectSetting> {
    const payload: BulkRequest<ProjectSetting> = { items: [] };

    pricingItems.forEach((pricingItem) => {
      const currentWebsiteSetting = currentSettings?.find((item) =>
        (pricingItem?.settingsId && item?.settingsId === pricingItem.settingsId) || item?.settingSlug === pricingItem.slug,
      );

      let settingValue: string | null;
      const rawValue = pricingItem?.value;
      if (rawValue == null) return;
      else if (Array.isArray(rawValue)) {
        settingValue = rawValue.join(', ');
      } else if (typeof rawValue === 'boolean') {
        settingValue = rawValue ? SettingsValues.active : SettingsValues.inactive;
      } else if (typeof rawValue === 'number') {
        settingValue = rawValue.toString();
      } else {
        settingValue = rawValue;
      }

      if (currentWebsiteSetting?.settingsValue !== settingValue) {
        const settingId = currentWebsiteSetting?.settingsId ?? pricingItem?.settingsId;
        if (!settingId) return;

        payload.items.push({
          settingsId: settingId,
          id: currentWebsiteSetting?.id,
          settingsValue: settingValue?.replace(/&nbsp;/g, ' ') || '',
          projectId,
        } as ProjectSetting);
      }
    });

    return payload;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ProjectSettingsViewService extends ReadOnlyCoreApiService<ProjectSettingsView> {

  constructor() {
    super('admin/view/project-settings');
  }
}
